import { useMerchant } from "@/app/providers/merchantProvider";
import MapPinIcon from "@/components/_icons/mapPinIcon";
import { ConfirmButton } from "@/components/button/confirmButton";
import { MerchantBusinessType, OrderType, ServiceType } from "@/constants/enum";
import { Address } from "@/dto/address";
import { ScheduledTime } from "@/dto/scheduledTime";
import { Service } from "@/dto/service";
import SmallErrorIcon from "@/components/_icons/smallErrorIcon";
import { cloverColorTheme } from "@/utils/cloverColorTheme";
import pom from "@/utils/pom";
import {
  getFromSessionStorage,
  saveToSessionStorage,
} from "@/utils/sessionStorageUtils";
import { LocalStorageKey, SessionStorageKey } from "@/utils/storageKeys";
import { applyDataTestId } from "@/utils/testUtils";
import {
  CloseButton,
  Popover,
  PopoverButton,
  PopoverPanel,
} from "@headlessui/react";
import { useTranslations } from "next-intl";
import { useEffect, useId, useState } from "react";
import Autocomplete from "react-google-autocomplete";
import { RegularModal } from "../modals/modals";
import { LoadingIndicator } from "@/app/(shop)/services/[slug]/components/loadingIndicator";

const PreferenceDropdown = ({ preference, setPreference }: any) => {
  const t = useTranslations("EditAddressesModal");
  return (
    <Popover className="relative">
      <PopoverButton
        className={[
          "flex",
          "items-center",
          "gap-x-2",
          "w-full",
          "leading-6",
          "text-gray-900",
          "hover:bg-gray-50",
        ].join(" ")}
      >
        <input
          type="text"
          id="delivery_preference_input"
          className="block px-2.5 h-[58px] py-2.5 w-full text-gray-900 bg-transparent rounded-[4px] border hover:border-black focus:outline-none focus:ring-1 focus:ring-blue border-gray-300 peer"
          value={
            preference === "contactless" ? t("contactless") : t("meetAtDoor")
          }
          onChange={(e) => setPreference(e.target.value)}
        />
        <label
          htmlFor="delivery_preference_input"
          className="absolute text-md text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-5 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-5 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1"
        >
          {t("deliveryPreference")}
        </label>
      </PopoverButton>
      <PopoverPanel
        transition
        className="absolute top-full z-20 w-full overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
      >
        <div className="p-3">
          <CloseButton
            className="group relative flex w-full items-center gap-x-6 rounded-lg p-3 leading-6 hover:bg-gray-50 text-left"
            onClick={() => {
              setPreference("contactless");
            }}
          >
            <div className="flex-auto">
              <p>{t("contactless")}</p>
            </div>
          </CloseButton>
          <CloseButton
            className="group relative flex w-full gap-x-6 rounded-lg p-3 leading-6 hover:bg-gray-50 text-left"
            onClick={() => {
              setPreference("meetDoor");
            }}
          >
            <div className="flex-auto">
              <p>{t("meetAtDoor")}</p>
            </div>
          </CloseButton>
        </div>
      </PopoverPanel>
    </Popover>
  );
};

interface EditAddressesModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onUpdated?: (a: any, b: boolean) => Promise<void>;
  address?: any;
  setDefaultDeliveryAllowed?: boolean;
  checkoutCloseHandler?: () => void;
  titleKey?: string;
  subtitleKey?: string;
}

export const EditAddressesModal = ({
  isOpen,
  onRequestClose,
  onUpdated,
  address,
  setDefaultDeliveryAllowed = false,
  checkoutCloseHandler,
  titleKey = "title",
  subtitleKey = "subtitle",
}: EditAddressesModalProps) => {
  const { merchant } = useMerchant();
  const merchantColor =
    merchant.merchantMeta?.colorPalette?.color ||
    cloverColorTheme.Main.primaryColor;
  const t = useTranslations("EditAddressesModal");
  const [addressOne, setAddressOne] = useState(address?.address1);
  const [addressTwo, setAddressTwo] = useState(address?.address2);
  const [city, setCity] = useState(address?.city);
  const [state, setState] = useState(address?.city);
  const [zip, setZip] = useState(address?.zip);
  const [tempAddress, setTempAddress] = useState(() => {
    if (address?.address1 && address?.city && address?.state) {
      return `${address.address1}, ${address.city}, ${address.state}`;
    }
    return "";
  });

  const [preference, setPreference] = useState(address?.preference);
  const [note, setNote] = useState(address?.note);
  const [showAddressEmptyError, setShowAddressEmptyError] =
    useState<boolean>(false);
  const [isDefaultDelivery, setIsDefaultDelivery] = useState<boolean>(false);
  const titleId = useId();
  const subtitleId = useId();
  const addressErrorId = useId();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (address) {
      setAddressOne(address?.address1);
      setAddressTwo(address?.address2);
      setCity(address?.city);
      setState(address?.state);
      setZip(address?.zip);
      setPreference(address?.preference);
      setNote(address?.note);
    }
  }, [address]);

  const handlePlaceSelected = (place: any) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(place?.adr_address, "text/html");
    const regionSpan = doc.querySelector("span.region");
    const zipSpan = doc.querySelector("span.postal-code");
    const stateTrimmed = regionSpan!.textContent;
    const zipTrimmed = zipSpan!.textContent;
    setTempAddress(place.formatted_address);
    setAddressOne(place.name);
    setCity(place?.vicinity.split(",").pop().trim());
    setState(stateTrimmed);
    setZip(zipTrimmed);
    setShowAddressEmptyError(false);
  };

  const handleTextChange = (event: any) => {
    setTempAddress(event.target.value);
    setShowAddressEmptyError(false);

    if (addressOne) {
      setAddressOne("");
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    if (addressOne === undefined || addressOne === "") {
      setIsLoading(false);
      setShowAddressEmptyError(false);

      setTimeout(() => {
        setShowAddressEmptyError(true);
      }, 10);
      return;
    }

    setShowAddressEmptyError(false);
    let address: Address = {
      address1: addressOne,
      city: city,
      state: state,
      country: "US",
      zip: zip,
      preference,
      note,
    };
    // address2 cannot be empty string as per olo-service and ecomm validation
    if (addressTwo) address = { ...address, address2: addressTwo };

    try {
      if (onUpdated) {
        await onUpdated(address, isDefaultDelivery);
      }
    } finally {
      setIsLoading(false);
      setAddressOne("");
      setTempAddress("");
    }
  };

  const closeModal = () => {
    setShowAddressEmptyError(false);
    const sessionTime = getFromSessionStorage<Partial<ScheduledTime>>(
      SessionStorageKey.SCHEDULED_TIME,
      {}
    );
    const merchantService = merchant?.services.find(
      (s: Service) =>
        s.type === ServiceType.PICKUP || s.type === ServiceType.WEBSITE
    );
    const deliveryService = merchant?.services.find(
      (s: Service) => s.type === ServiceType.DELIVERY
    );

    const retailServices = [
      { type: OrderType.PICKUP, service: merchantService },
      { type: OrderType.DELIVERY, service: deliveryService },
    ];

    const enabledServices = retailServices.filter(
      ({ service }) => service?.enabled
    );

    let enabledOrderType = "";

    switch (merchant.merchantBusinessType) {
      case MerchantBusinessType.RETAIL:
        if (enabledServices.length >= 1) {
          enabledOrderType = enabledServices[0].type;
        } else {
          enabledOrderType = OrderType.PICKUP;
        }
        break;
      case MerchantBusinessType.RESTAURANT:
        // Check if only Delivery is enabled
        const isPickupEnabled = merchantService?.enabled;
        const isOnlyDeliveryEnabled =
          enabledServices.length === 1 && deliveryService?.enabled;

        if (isPickupEnabled) {
          enabledOrderType = OrderType.PICKUP;
        } else if (isOnlyDeliveryEnabled) {
          enabledOrderType = OrderType.DELIVERY;
        }
        break;
      default:
        enabledOrderType = OrderType.PICKUP;
        break;
    }

    saveToSessionStorage(SessionStorageKey.SCHEDULED_TIME, {
      orderType: enabledOrderType || OrderType.PICKUP,
      date: sessionTime?.date || null,
      time: sessionTime?.time || null,
    });

    if (merchant?.isRetail) {
      window.dispatchEvent(
        new StorageEvent("storage", { key: LocalStorageKey.PICKUP_ORDER_TYPE })
      );
    }
    checkoutCloseHandler?.();
    onRequestClose?.();
  };

  const handleBlur = () => {
    if (
      !tempAddress ||
      tempAddress.trim() === "" ||
      addressOne === undefined ||
      addressOne === ""
    ) {
      setShowAddressEmptyError(true);
    } else {
      setShowAddressEmptyError(false);
    }
  };

  return (
    <RegularModal
      isOpen={isOpen}
      onRequestClose={closeModal}
      overlayClassName="bg-opacity-50"
      contentClassName="bg-white sm:rounded-lg shadow-lg w-[100%] h-[100%] sm:h-[auto] sm:w-[70%] mx-auto overflow-hidden pt-6"
      data-testid={applyDataTestId(pom.modals.editAddressModal)}
      contentLabel={t("dialogLabel")}
      focusElementId={titleId}
    >
      <div
        data-testid={applyDataTestId(pom.modals.editAddressModal)}
        className="px-4 pb-4 flex flex-col justify-center items-center"
      >
        <MapPinIcon brandingColor={merchantColor} />
        <h2
          id={titleId}
          tabIndex={-1}
          aria-describedby={subtitleId}
          className="mt-4 text-3xl font-medium text-center text-[#373737] text-[32px]"
        >
          {t(titleKey)}
        </h2>
        <p
          id={subtitleId}
          className="mt-4 text-base leading-6 text-center text-[15px] text-neutral-700 w-full"
        >
          {t(subtitleKey)}
        </p>

        <form
          className="w-full flex flex-col items-center"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className="pt-10 w-full sm:w-[80%] grid sm:grid-cols-2 gap-y-8 sm:gap-x-6 m-auto">
            <div className="relative">
              <div>
                <Autocomplete
                  apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || ""}
                  onChange={handleTextChange}
                  onPlaceSelected={handlePlaceSelected}
                  onBlur={handleBlur}
                  aria-invalid={showAddressEmptyError}
                  aria-describedby={
                    showAddressEmptyError ? addressErrorId : undefined
                  }
                  id="delivery_address1_input"
                  options={{
                    types: ["address"],
                    fields: ["all"],
                  }}
                  value={tempAddress}
                  className={`block px-2.5 h-[58px] py-2.5 w-full text-gray-900 bg-transparent rounded-[4px] border hover:border-black focus:outline-none focus:ring-1 ${
                    showAddressEmptyError
                      ? "border-red-600 focus:ring-red-600"
                      : "border-gray-300 focus:ring-blue"
                  } peer`}
                />
                <label
                  htmlFor="delivery_address1_input"
                  className={`absolute text-md duration-300 transform -translate-y-5 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 ${
                    showAddressEmptyError
                      ? "text-red-600 peer-focus:text-red-600"
                      : "text-gray-500 peer-focus:text-blue-600"
                  }`}
                >
                  {t("deliveryAddress")}
                </label>
              </div>

              {showAddressEmptyError && (
                <div
                  id={addressErrorId}
                  role="alert"
                  aria-live="assertive"
                  aria-atomic="true"
                  className="absolute flex flex-row gap-2 top-full mt-1 text-sm text-red-600"
                >
                  <SmallErrorIcon aria-hidden="true" />
                  {t("invalidAddress")}
                </div>
              )}
            </div>
            <div className="relative">
              <input
                type="text"
                id="delivery_address2_input"
                className="block px-2.5 h-[58px] py-2.5 w-full text-gray-900 bg-transparent rounded-[4px] border hover:border-black focus:outline-none focus:ring-1 focus:ring-blue border-gray-300 peer"
                value={addressTwo}
                onChange={(e) => setAddressTwo(e.target.value)}
              />
              <label
                htmlFor="delivery_address2_input"
                className="absolute text-md text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-5 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-5 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1"
              >
                {t("aptSuite")}
              </label>
            </div>
            <div className="relative">
              <PreferenceDropdown
                preference={preference}
                setPreference={setPreference}
              />
            </div>
            <div className="relative">
              <input
                type="text"
                id="delivery_instructions_input"
                className="block px-2.5 h-[58px] py-2.5 w-full text-gray-900 bg-transparent rounded-[4px] border hover:border-black focus:outline-none focus:ring-1 focus:ring-blue border-gray-300 peer"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <label
                htmlFor="delivery_instructions_input"
                className="absolute text-md text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-5 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-5 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1"
              >
                {t("deliveryInstructions")}
              </label>
            </div>
          </div>
          {setDefaultDeliveryAllowed && (
            <div className="flex row-auto items-center mt-10">
              <input
                id="set-default-delivery-input"
                type="checkbox"
                checked={isDefaultDelivery}
                onChange={(e) => {
                  setIsDefaultDelivery(e.target.checked);
                }}
                className="group block size-8 rounded border bg-white data-[checked]:bg-blue-500 cursor-pointer"
              />
              <label
                htmlFor="set-default-delivery-input"
                className="ml-2 cursor-pointer select-none"
              >
                {t("setDefault")}
              </label>
            </div>
          )}
          {isLoading ? (
            <div className="fixed bottom-0 sm:static">
              <LoadingIndicator />
            </div>
          ) : (
            <ConfirmButton
              className="rounded-none fixed sm:static bottom-0 w-full px-16 py-5 sm:mt-11 max-w-full text-xl font-medium leading-7 text-center text-white whitespace-nowrap bg-[#228800] disabled:bg-[#C7C7C7] sm:rounded-lg sm:w-[250px] max-md:px-5 sm:max-md:mt-10 shrink"
              onClick={() => {
                handleSubmit();
              }}
              type="submit"
            >
              <p>{t("save")}</p>
            </ConfirmButton>
          )}
        </form>
      </div>
    </RegularModal>
  );
};
